import { useEffect, useRef } from 'react';
import { usePostHog } from '@posthog/react';
import type { User } from '@supabase/supabase-js';
import type { Organization } from '../types/tenant';

/**
 * Hook to identify users and associate them with organizations in PostHog.
 *
 * - Calls posthog.identify() when a user signs in
 * - Calls posthog.group() when user has an active organization
 * - Tracks previous values to avoid redundant API calls
 */
export function usePostHogIdentify(
  user: User | null,
  org?: Pick<Organization, 'id' | 'name' | 'subscription'> | null
): void {
  const posthog = usePostHog();
  const lastIdentifiedUserId = useRef<string | null>(null);
  const lastGroupedOrgId = useRef<string | null>(null);

  // Identify user when they sign in
  useEffect(() => {
    if (!posthog) return;

    if (user && user.id !== lastIdentifiedUserId.current) {
      posthog.identify(user.id, {
        email: user.email,
        created_at: user.created_at,
        auth_provider: user.app_metadata?.provider,
      });
      lastIdentifiedUserId.current = user.id;
    }
  }, [posthog, user?.id, user?.email, user?.created_at, user?.app_metadata?.provider]);

  // Group user by organization when org changes
  useEffect(() => {
    if (!posthog || !user || !org?.id) return;

    if (org.id !== lastGroupedOrgId.current) {
      posthog.group('organization', org.id, {
        name: org.name,
        plan: org.subscription.plan,
        subscription_status: org.subscription.status,
      });
      lastGroupedOrgId.current = org.id;
    }
  }, [posthog, user, org?.id, org?.name, org?.subscription.plan, org?.subscription.status]);
}
